/**
 * Email Template Builder (v56) — блочный конструктор email-шаблонов.
 *
 * 4 редактируемых блока (header / image / body / footer), каждый с собственным
 * `enabled` toggle. Порядок фиксирован. Live-preview справа через iframe-srcDoc.
 *
 * Структура blocks хранится в `localStorage[email-templates-v1-{projectId}]`
 * (Phase 1, как было). Phase 2 — переезд на sender_test_db.templates.blocks_json.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Accordion,
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  InputGroup,
  Modal,
  Row,
} from 'react-bootstrap';

import {
  EMAIL_IMAGE_OPTIONS,
  EmailBlocks,
  defaultEmailBlocks,
  renderBlocks,
} from './baseTemplate';

interface EmailTemplateBuilderProps {
  mnemonic: string;
  blocks: EmailBlocks;
  assetsBaseUrl?: string;
  onSave: (mnemonic: string, blocks: EmailBlocks) => void;
  onCancel: () => void;
}

/** Извлекает все %%param%% из переданных строк (для подсказки оператору). */
function extractParams(blocks: EmailBlocks): string[] {
  const sources = [
    blocks.body.title,
    blocks.body.description,
    blocks.body.additionalText || '',
    blocks.body.button.text,
    blocks.body.button.url,
    blocks.body.linkText || '',
    blocks.body.linkUrl || '',
  ];
  const re = /%%([^%]+)%%/g;
  const set = new Set<string>();
  sources.forEach((s) => {
    let m;
    while ((m = re.exec(s)) !== null) set.add(m[1]);
  });
  return Array.from(set).sort();
}

const SECTION_LABELS = {
  header: '1. Шапка (логотип + название)',
  image: '2. Картинка',
  body: '3. Текст и кнопка',
  footer: '4. Подвал (юр. инфо + кнопки магазинов)',
};

export const EmailTemplateBuilder: React.FC<EmailTemplateBuilderProps> = ({
  mnemonic: initialMnemonic,
  blocks: initialBlocks,
  assetsBaseUrl,
  onSave,
  onCancel,
}) => {
  const [mnemonic, setMnemonic] = useState(initialMnemonic);
  const [blocks, setBlocks] = useState<EmailBlocks>(initialBlocks);
  const [fullscreen, setFullscreen] = useState(false);

  // При смене initialBlocks (выбран другой шаблон извне) — синхронизируем.
  useEffect(() => {
    setBlocks(initialBlocks);
    setMnemonic(initialMnemonic);
  }, [initialBlocks, initialMnemonic]);

  const html = useMemo(() => renderBlocks(blocks), [blocks]);
  const params = useMemo(() => extractParams(blocks), [blocks]);

  // Список картинок с полными URL.
  const imageOptions = useMemo(() => {
    const base = assetsBaseUrl || 'https://trientes.com/img/email-assets';
    return EMAIL_IMAGE_OPTIONS.map((opt) => ({
      ...opt,
      url: `${base}${opt.suffix}`,
    }));
  }, [assetsBaseUrl]);

  const updateBlocks = (mutator: (b: EmailBlocks) => void) => {
    setBlocks((prev) => {
      const next = JSON.parse(JSON.stringify(prev)) as EmailBlocks;
      mutator(next);
      return next;
    });
  };

  const copyHTML = () => {
    navigator.clipboard.writeText(html).then(
      () => alert('HTML скопирован в буфер обмена'),
      () => alert('Не удалось скопировать'),
    );
  };

  return (
    <Card className="mb-3">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center gap-2">
          <strong>Конструктор email-шаблона</strong>
          {mnemonic && <Badge bg="secondary">{mnemonic}</Badge>}
        </div>
        <div className="d-flex gap-2">
          <Button size="sm" variant="outline-secondary" onClick={() => setFullscreen(true)}>
            Полноэкранный просмотр
          </Button>
          <Button size="sm" variant="outline-secondary" onClick={copyHTML}>
            Скопировать HTML
          </Button>
          <Button size="sm" variant="outline-secondary" onClick={onCancel}>
            Отмена
          </Button>
          <Button
            size="sm"
            variant="primary"
            onClick={() => onSave(mnemonic.trim(), blocks)}
            disabled={!mnemonic.trim()}
          >
            Сохранить
          </Button>
        </div>
      </Card.Header>
      <Card.Body>
        <Row className="g-3">
          {/* Левая часть — настройки блоков */}
          <Col md={6}>
            <Form.Group className="mb-3">
              <Form.Label className="small mb-1">Мнемоника шаблона</Form.Label>
              <Form.Control
                size="sm"
                value={mnemonic}
                onChange={(e) => setMnemonic(e.target.value.replace(/[^a-z0-9_]/g, ''))}
                placeholder="welcome_email"
              />
              <Form.Text muted>Только латинские буквы, цифры, underscore.</Form.Text>
            </Form.Group>

            {params.length > 0 && (
              <Alert variant="info" className="py-2 small">
                <strong>Параметры в шаблоне ({params.length}):</strong>{' '}
                {params.map((p) => (
                  <Badge key={p} bg="primary" className="me-1">
                    %%{p}%%
                  </Badge>
                ))}
              </Alert>
            )}

            <Accordion defaultActiveKey="body" alwaysOpen>
              {/* Header */}
              <Accordion.Item eventKey="header">
                <Accordion.Header>
                  <div className="d-flex justify-content-between align-items-center w-100 me-2">
                    <span>{SECTION_LABELS.header}</span>
                    <Badge bg={blocks.header.enabled ? 'success' : 'secondary'}>
                      {blocks.header.enabled ? 'вкл' : 'выкл'}
                    </Badge>
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  <Form.Check
                    type="switch"
                    label="Показывать блок"
                    checked={blocks.header.enabled}
                    onChange={(e) => updateBlocks((b) => { b.header.enabled = e.target.checked; })}
                    className="mb-2"
                  />
                  <Form.Group className="mb-2">
                    <Form.Label className="small mb-1">Название бренда</Form.Label>
                    <Form.Control
                      size="sm"
                      value={blocks.header.brandName}
                      onChange={(e) => updateBlocks((b) => { b.header.brandName = e.target.value; })}
                    />
                  </Form.Group>
                  <Form.Group className="mb-2">
                    <Form.Label className="small mb-1">URL логотипа (40x40)</Form.Label>
                    <Form.Control
                      size="sm"
                      value={blocks.header.logoUrl}
                      onChange={(e) => updateBlocks((b) => { b.header.logoUrl = e.target.value; })}
                    />
                  </Form.Group>
                  <Row className="g-2">
                    <Col xs={6}>
                      <Form.Label className="small mb-1">Цвет фона</Form.Label>
                      <Form.Control
                        type="color"
                        size="sm"
                        value={blocks.header.backgroundColor}
                        onChange={(e) => updateBlocks((b) => { b.header.backgroundColor = e.target.value; })}
                      />
                    </Col>
                    <Col xs={6}>
                      <Form.Label className="small mb-1">Цвет текста</Form.Label>
                      <Form.Control
                        type="color"
                        size="sm"
                        value={blocks.header.textColor}
                        onChange={(e) => updateBlocks((b) => { b.header.textColor = e.target.value; })}
                      />
                    </Col>
                  </Row>
                  <Form.Check
                    type="switch"
                    label="Email-иконка справа"
                    checked={blocks.header.showEmailIcon}
                    onChange={(e) => updateBlocks((b) => { b.header.showEmailIcon = e.target.checked; })}
                    className="mt-2"
                  />
                  <Form.Group className="mt-2">
                    <Form.Label className="small mb-1">Дополнительный CSS (advanced)</Form.Label>
                    <Form.Control
                      size="sm"
                      as="textarea"
                      rows={2}
                      placeholder="border-top: 4px solid #d94a29;"
                      value={blocks.header.customCss || ''}
                      onChange={(e) => updateBlocks((b) => { b.header.customCss = e.target.value; })}
                    />
                    <Form.Text muted>Вставляется в style outer cell блока. Без точек с запятой в конце не обязательно.</Form.Text>
                  </Form.Group>
                </Accordion.Body>
              </Accordion.Item>

              {/* Image */}
              <Accordion.Item eventKey="image">
                <Accordion.Header>
                  <div className="d-flex justify-content-between align-items-center w-100 me-2">
                    <span>{SECTION_LABELS.image}</span>
                    <Badge bg={blocks.image.enabled ? 'success' : 'secondary'}>
                      {blocks.image.enabled ? 'вкл' : 'выкл'}
                    </Badge>
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  <Form.Check
                    type="switch"
                    label="Показывать блок"
                    checked={blocks.image.enabled}
                    onChange={(e) => updateBlocks((b) => { b.image.enabled = e.target.checked; })}
                    className="mb-2"
                  />
                  <Form.Group className="mb-2">
                    <Form.Label className="small mb-1">Выбрать из библиотеки</Form.Label>
                    <Form.Select
                      size="sm"
                      value={imageOptions.find((o) => o.url === blocks.image.imageUrl)?.key || 'custom'}
                      onChange={(e) => {
                        const key = e.target.value;
                        if (key === 'custom') return;
                        const opt = imageOptions.find((o) => o.key === key);
                        if (opt) updateBlocks((b) => { b.image.imageUrl = opt.url; });
                      }}
                    >
                      <option value="custom">— внешний URL —</option>
                      {imageOptions.map((opt) => (
                        <option key={opt.key} value={opt.key}>
                          {opt.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                  <Form.Group className="mb-2">
                    <Form.Label className="small mb-1">URL картинки</Form.Label>
                    <Form.Control
                      size="sm"
                      value={blocks.image.imageUrl}
                      onChange={(e) => updateBlocks((b) => { b.image.imageUrl = e.target.value; })}
                    />
                  </Form.Group>
                  <Form.Group className="mb-2">
                    <Form.Label className="small mb-1">Alt-текст</Form.Label>
                    <Form.Control
                      size="sm"
                      value={blocks.image.altText}
                      onChange={(e) => updateBlocks((b) => { b.image.altText = e.target.value; })}
                    />
                  </Form.Group>
                  <Row className="g-2">
                    <Col xs={4}>
                      <Form.Label className="small mb-1">Ширина (px)</Form.Label>
                      <Form.Control
                        type="number"
                        size="sm"
                        value={blocks.image.width}
                        onChange={(e) => updateBlocks((b) => { b.image.width = parseInt(e.target.value, 10) || 0; })}
                      />
                    </Col>
                    <Col xs={4}>
                      <Form.Label className="small mb-1">Высота (px)</Form.Label>
                      <Form.Control
                        type="number"
                        size="sm"
                        value={blocks.image.height}
                        onChange={(e) => updateBlocks((b) => { b.image.height = parseInt(e.target.value, 10) || 0; })}
                      />
                    </Col>
                    <Col xs={4}>
                      <Form.Label className="small mb-1">Радиус кadr (px)</Form.Label>
                      <Form.Control
                        type="number"
                        size="sm"
                        value={blocks.image.imageRadius}
                        onChange={(e) => updateBlocks((b) => { b.image.imageRadius = parseInt(e.target.value, 10) || 0; })}
                      />
                    </Col>
                  </Row>
                  <Form.Group className="mt-2">
                    <Form.Label className="small mb-1">Фон рамки (CSS color)</Form.Label>
                    <InputGroup size="sm">
                      <Form.Control
                        size="sm"
                        value={blocks.image.frameBackground}
                        onChange={(e) => updateBlocks((b) => { b.image.frameBackground = e.target.value; })}
                        placeholder="rgba(113,108,108,0.12) или transparent"
                      />
                      <Button
                        size="sm"
                        variant="outline-secondary"
                        onClick={() => updateBlocks((b) => { b.image.frameBackground = 'transparent'; })}
                      >
                        Без рамки
                      </Button>
                    </InputGroup>
                  </Form.Group>
                  <Form.Group className="mt-2">
                    <Form.Label className="small mb-1">Дополнительный CSS</Form.Label>
                    <Form.Control
                      size="sm"
                      as="textarea"
                      rows={2}
                      value={blocks.image.customCss || ''}
                      onChange={(e) => updateBlocks((b) => { b.image.customCss = e.target.value; })}
                    />
                  </Form.Group>
                </Accordion.Body>
              </Accordion.Item>

              {/* Body */}
              <Accordion.Item eventKey="body">
                <Accordion.Header>
                  <div className="d-flex justify-content-between align-items-center w-100 me-2">
                    <span>{SECTION_LABELS.body}</span>
                    <Badge bg={blocks.body.enabled ? 'success' : 'secondary'}>
                      {blocks.body.enabled ? 'вкл' : 'выкл'}
                    </Badge>
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  <Form.Check
                    type="switch"
                    label="Показывать блок"
                    checked={blocks.body.enabled}
                    onChange={(e) => updateBlocks((b) => { b.body.enabled = e.target.checked; })}
                    className="mb-2"
                  />
                  <Form.Group className="mb-2">
                    <Form.Label className="small mb-1">Заголовок</Form.Label>
                    <Form.Control
                      size="sm"
                      value={blocks.body.title}
                      onChange={(e) => updateBlocks((b) => { b.body.title = e.target.value; })}
                    />
                  </Form.Group>
                  <Row className="g-2 mb-2">
                    <Col xs={6}>
                      <Form.Label className="small mb-1">Цвет заголовка</Form.Label>
                      <Form.Control
                        type="color"
                        size="sm"
                        value={blocks.body.titleColor}
                        onChange={(e) => updateBlocks((b) => { b.body.titleColor = e.target.value; })}
                      />
                    </Col>
                    <Col xs={6}>
                      <Form.Label className="small mb-1">Размер (px)</Form.Label>
                      <Form.Control
                        type="number"
                        size="sm"
                        value={blocks.body.titleSize}
                        onChange={(e) => updateBlocks((b) => { b.body.titleSize = parseInt(e.target.value, 10) || 28; })}
                      />
                    </Col>
                  </Row>
                  <Form.Group className="mb-2">
                    <Form.Label className="small mb-1">Описание (поддерживает %%param%%)</Form.Label>
                    <Form.Control
                      size="sm"
                      as="textarea"
                      rows={3}
                      value={blocks.body.description}
                      onChange={(e) => updateBlocks((b) => { b.body.description = e.target.value; })}
                    />
                  </Form.Group>
                  <Row className="g-2 mb-2">
                    <Col xs={6}>
                      <Form.Label className="small mb-1">Текст ссылки в описании</Form.Label>
                      <Form.Control
                        size="sm"
                        value={blocks.body.linkText || ''}
                        onChange={(e) => updateBlocks((b) => { b.body.linkText = e.target.value; })}
                      />
                    </Col>
                    <Col xs={6}>
                      <Form.Label className="small mb-1">URL ссылки</Form.Label>
                      <Form.Control
                        size="sm"
                        value={blocks.body.linkUrl || ''}
                        onChange={(e) => updateBlocks((b) => { b.body.linkUrl = e.target.value; })}
                      />
                    </Col>
                  </Row>

                  <hr className="my-3" />
                  <strong className="small d-block mb-2">Кнопка CTA</strong>
                  <Form.Check
                    type="switch"
                    label="Показывать кнопку"
                    checked={blocks.body.button.enabled}
                    onChange={(e) => updateBlocks((b) => { b.body.button.enabled = e.target.checked; })}
                    className="mb-2"
                  />
                  <Row className="g-2 mb-2">
                    <Col xs={6}>
                      <Form.Label className="small mb-1">Текст кнопки</Form.Label>
                      <Form.Control
                        size="sm"
                        value={blocks.body.button.text}
                        onChange={(e) => updateBlocks((b) => { b.body.button.text = e.target.value; })}
                      />
                    </Col>
                    <Col xs={6}>
                      <Form.Label className="small mb-1">URL кнопки</Form.Label>
                      <Form.Control
                        size="sm"
                        value={blocks.body.button.url}
                        onChange={(e) => updateBlocks((b) => { b.body.button.url = e.target.value; })}
                      />
                    </Col>
                  </Row>
                  <Row className="g-2 mb-2">
                    <Col xs={4}>
                      <Form.Label className="small mb-1">Цвет фона</Form.Label>
                      <Form.Control
                        type="color"
                        size="sm"
                        value={blocks.body.button.background}
                        onChange={(e) => updateBlocks((b) => { b.body.button.background = e.target.value; })}
                      />
                    </Col>
                    <Col xs={4}>
                      <Form.Label className="small mb-1">Цвет текста</Form.Label>
                      <Form.Control
                        type="color"
                        size="sm"
                        value={blocks.body.button.textColor}
                        onChange={(e) => updateBlocks((b) => { b.body.button.textColor = e.target.value; })}
                      />
                    </Col>
                    <Col xs={4}>
                      <Form.Label className="small mb-1">Радиус (px)</Form.Label>
                      <Form.Control
                        type="number"
                        size="sm"
                        value={blocks.body.button.borderRadius}
                        onChange={(e) => updateBlocks((b) => { b.body.button.borderRadius = parseInt(e.target.value, 10) || 48; })}
                      />
                    </Col>
                  </Row>

                  <hr className="my-3" />
                  <Form.Group className="mb-2">
                    <Form.Label className="small mb-1">Дополнительный текст (под кнопкой)</Form.Label>
                    <Form.Control
                      size="sm"
                      as="textarea"
                      rows={2}
                      value={blocks.body.additionalText || ''}
                      onChange={(e) => updateBlocks((b) => { b.body.additionalText = e.target.value; })}
                    />
                  </Form.Group>
                  <Form.Group className="mt-2">
                    <Form.Label className="small mb-1">Дополнительный CSS</Form.Label>
                    <Form.Control
                      size="sm"
                      as="textarea"
                      rows={2}
                      value={blocks.body.customCss || ''}
                      onChange={(e) => updateBlocks((b) => { b.body.customCss = e.target.value; })}
                    />
                  </Form.Group>
                </Accordion.Body>
              </Accordion.Item>

              {/* Footer */}
              <Accordion.Item eventKey="footer">
                <Accordion.Header>
                  <div className="d-flex justify-content-between align-items-center w-100 me-2">
                    <span>{SECTION_LABELS.footer}</span>
                    <Badge bg={blocks.footer.enabled ? 'success' : 'secondary'}>
                      {blocks.footer.enabled ? 'вкл' : 'выкл'}
                    </Badge>
                  </div>
                </Accordion.Header>
                <Accordion.Body>
                  <Form.Check
                    type="switch"
                    label="Показывать блок"
                    checked={blocks.footer.enabled}
                    onChange={(e) => updateBlocks((b) => { b.footer.enabled = e.target.checked; })}
                    className="mb-2"
                  />
                  <Form.Group className="mb-2">
                    <Form.Label className="small mb-1">Юридическая информация (\n = новая строка)</Form.Label>
                    <Form.Control
                      size="sm"
                      as="textarea"
                      rows={8}
                      value={blocks.footer.legalInfo}
                      onChange={(e) => updateBlocks((b) => { b.footer.legalInfo = e.target.value; })}
                    />
                  </Form.Group>
                  <Form.Group className="mb-2">
                    <Form.Label className="small mb-1">Цвет текста футера</Form.Label>
                    <Form.Control
                      size="sm"
                      value={blocks.footer.legalColor}
                      onChange={(e) => updateBlocks((b) => { b.footer.legalColor = e.target.value; })}
                    />
                  </Form.Group>

                  <Form.Check
                    type="switch"
                    label="Кнопки App Store / Google Play"
                    checked={blocks.footer.showAppBadges}
                    onChange={(e) => updateBlocks((b) => { b.footer.showAppBadges = e.target.checked; })}
                    className="mb-2"
                  />
                  {blocks.footer.showAppBadges && (
                    <Row className="g-2 mb-2">
                      <Col xs={6}>
                        <Form.Label className="small mb-1">App Store URL</Form.Label>
                        <Form.Control
                          size="sm"
                          value={blocks.footer.appStoreUrl}
                          onChange={(e) => updateBlocks((b) => { b.footer.appStoreUrl = e.target.value; })}
                        />
                      </Col>
                      <Col xs={6}>
                        <Form.Label className="small mb-1">Google Play URL</Form.Label>
                        <Form.Control
                          size="sm"
                          value={blocks.footer.googlePlayUrl}
                          onChange={(e) => updateBlocks((b) => { b.footer.googlePlayUrl = e.target.value; })}
                        />
                      </Col>
                    </Row>
                  )}
                  <Row className="g-2 mb-2">
                    <Col xs={8}>
                      <Form.Label className="small mb-1">Текст копирайта</Form.Label>
                      <Form.Control
                        size="sm"
                        value={blocks.footer.copyrightText}
                        onChange={(e) => updateBlocks((b) => { b.footer.copyrightText = e.target.value; })}
                      />
                    </Col>
                    <Col xs={4}>
                      <Form.Label className="small mb-1">Год</Form.Label>
                      <Form.Control
                        size="sm"
                        value={blocks.footer.year}
                        onChange={(e) => updateBlocks((b) => { b.footer.year = e.target.value; })}
                      />
                    </Col>
                  </Row>
                  <Form.Group className="mt-2">
                    <Form.Label className="small mb-1">Дополнительный CSS</Form.Label>
                    <Form.Control
                      size="sm"
                      as="textarea"
                      rows={2}
                      value={blocks.footer.customCss || ''}
                      onChange={(e) => updateBlocks((b) => { b.footer.customCss = e.target.value; })}
                    />
                  </Form.Group>
                </Accordion.Body>
              </Accordion.Item>
            </Accordion>

            <div className="mt-3">
              <Button
                size="sm"
                variant="outline-warning"
                onClick={() => {
                  if (window.confirm('Сбросить все настройки шаблона до дефолтных?')) {
                    setBlocks(defaultEmailBlocks(assetsBaseUrl));
                  }
                }}
              >
                Сбросить к дефолту
              </Button>
            </div>
          </Col>

          {/* Правая часть — превью */}
          <Col md={6}>
            <div className="position-sticky" style={{ top: 0 }}>
              <div className="small text-muted mb-2">
                Превью (600px → скейл по ширине колонки):
              </div>
              <div
                style={{
                  border: '1px solid #dee2e6',
                  borderRadius: 8,
                  overflow: 'hidden',
                  background: '#1a1a2e',
                }}
              >
                <iframe
                  title="email-preview"
                  srcDoc={html}
                  style={{
                    width: '100%',
                    height: 700,
                    border: 'none',
                    background: 'transparent',
                  }}
                />
              </div>
            </div>
          </Col>
        </Row>
      </Card.Body>

      <Modal show={fullscreen} onHide={() => setFullscreen(false)} fullscreen>
        <Modal.Header closeButton>
          <Modal.Title>{mnemonic} — полноэкранный просмотр</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: '#1a1a2e', padding: 20 }}>
          <iframe
            title="email-fullscreen"
            srcDoc={html}
            style={{ width: '100%', height: '100%', border: 'none', background: '#1a1a2e' }}
          />
        </Modal.Body>
      </Modal>
    </Card>
  );
};

export default EmailTemplateBuilder;
